import React, { FC, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Popover } from '@mui/material';
import { useDivisionSwitch } from 'src/providers/DivisionSwitchProvider';
import { useStacklineTheme } from '@stackline/ui';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { NotificationBadge } from 'src/components/common/NotificationBadge';
import { useAppSelector, useHistory } from 'src/utils/Hooks';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import {
  getDivisions,
  getCurrentDivisionId,
  saveCurrentDivisionId,
  saveDivisions,
  CURRENT_DIVISION_KEY
} from 'src/utils/divisionStorage';
import {
  v2SessionInfo,
  v2GetDivisions,
  v2SwitchDivision,
  V2SessionInfoResponse,
  DivisionMembership,
} from 'src/store/modules/auth/v2Operations';
import { DIVISION_VALIDATED_EVENT } from 'src/hooks/useDivisionCheck';
import { isV2Enabled } from 'src/utils/authVersion';
import { buildPostSwitchUrl } from 'src/utils/divisionEntityRewrite';
import { getSsoReferrer } from 'src/utils/ssoReferrer';

interface AccountDivisionPopoverProps {
  appName: string;
}

const SEARCH_ICON = (
  <svg viewBox="0 0 24 24" fill="none" width="16" height="16" style={{ flexShrink: 0, opacity: 0.18 }}>
    <circle cx="11" cy="11" r="8" stroke="#7E8FA8" strokeWidth="2" />
    <path d="M21 21l-4.35-4.35" stroke="#7E8FA8" strokeWidth="2" />
  </svg>
);

// Right-pointing chevron for the collapsed division row -- click to drill
// into the nested division list. Mirrors the chevron in the Figma collapsed
// state at https://www.figma.com/design/I5M4w7d9IgO4MoqLRtYoTE/...?node-id=3061-9937
const CHEVRON_RIGHT = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <path d="m10 8 4 4-4 4" stroke="#031425" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Left-pointing chevron for the "Back" row in the nested division view.
const CHEVRON_LEFT = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <path d="m14 8-4 4 4 4" stroke="#031425" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WARNING_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      stroke="#F35379"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const DISMISSED_MISMATCH_KEY = 'sl-division-mismatch-dismissed';

function formatDivisionLabel(
  d: { companyName?: string; name?: string } | undefined | null,
  fallback: string
): string {
  if (!d) return fallback;
  const joined = [d.companyName, d.name]
    .filter((s): s is string => Boolean(s && s.trim()))
    .join(' ')
    .trim();
  return joined || fallback;
}

function isDismissedMismatch(divisionId: string): boolean {
  try { return sessionStorage.getItem(DISMISSED_MISMATCH_KEY) === divisionId; } catch { return false; }
}

function setDismissedMismatch(divisionId: string): void {
  try { sessionStorage.setItem(DISMISSED_MISMATCH_KEY, divisionId); } catch {}
}

function clearDismissedMismatch(): void {
  try { sessionStorage.removeItem(DISMISSED_MISMATCH_KEY); } catch {}
}

const AccountDivisionPopover: FC<AccountDivisionPopoverProps> = ({ appName }) => {
  const isV2 = isV2Enabled();
  const history = useHistory();
  const theme = useStacklineTheme();
  const avatarRef = useRef<HTMLDivElement>(null);

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  // Nested-picker state: 'menu' shows email/role + collapsed division row +
  // settings/sign out; 'divisions' replaces the body with the searchable
  // division list + a back row. Mirrors the Figma drill-down pattern.
  const [view, setView] = useState<'menu' | 'divisions'>('menu');
  const { beginSwitch, endSwitch } = useDivisionSwitch();
  const [searchQuery, setSearchQuery] = useState('');
  const [divisions, setDivisions] = useState<DivisionMembership[] | null>(() => getDivisions());
  const [activeDivisionId, setActiveDivisionId] = useState<string | null>(() => getCurrentDivisionId());

  // Cross-tab division change detection (2a)
  const [divisionMismatch, setDivisionMismatch] = useState(false);
  const [mismatchDivisionName, setMismatchDivisionName] = useState<string | null>(null);
  const [mismatchDivisionId, setMismatchDivisionId] = useState<string | null>(null);
  const [alertAnchor, setAlertAnchor] = useState<HTMLElement | null>(null);

  const { searchParams } = useAppSelector((state) => state.app.queryParams);
  const session = useAppSelector((state) => state.user?.session);
  const { email, isAdmin } = session || {};
  const reduxDivisionId = session?.divisionId;
  const currentDivisionId = activeDivisionId || reduxDivisionId;

  const shouldShowNotificationBadge = useNotificationsBadge(
    (state) =>
      state.notificationBadges.presentations || state.notificationBadges.export || state.notificationBadges.adjustments
  );

  const role = isAdmin ? 'Admin' : 'User';

  // Fetch fresh divisions on mount.
  // Replaces the previous v2Validate call with parallel session-info +
  // /v2/divisions; the active division id comes from session-info, the
  // list from /v2/divisions.
  useEffect(() => {
    if (!isV2) return;
    const fetchDivisions = async () => {
      try {
        const [sessionInfo, divisionsResp] = await Promise.all([
          v2SessionInfo(),
          v2GetDivisions().catch(() => null),
        ]);
        if (sessionInfo.authenticated && divisionsResp?.divisions?.length) {
          saveDivisions(divisionsResp.divisions);
          setDivisions(divisionsResp.divisions);
          if (sessionInfo.divisionId) {
            setActiveDivisionId(sessionInfo.divisionId);
          }
        }
      } catch (error) {
        console.error('[AccountDivisionPopover] Failed to fetch divisions:', error);
      }
    };
    fetchDivisions();
  }, [isV2]);

  // 2a: Listen for cross-tab division changes via localStorage (same-origin)
  // and v2Validate on visibilitychange (cross-origin, e.g. Advisor switched)
  const mismatchAlreadyDetectedRef = useRef(false);

  useEffect(() => {
    if (!isV2) return;

    const triggerMismatch = (newId: string, newName: string) => {
      setDivisionMismatch(true);
      setMismatchDivisionName(newName);
      setMismatchDivisionId(newId);
      if (!mismatchAlreadyDetectedRef.current) {
        mismatchAlreadyDetectedRef.current = true;
        setAlertAnchor(avatarRef.current);
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== CURRENT_DIVISION_KEY) return;
      const newDivisionId = e.newValue;
      if (newDivisionId && newDivisionId !== currentDivisionId && !isDismissedMismatch(newDivisionId)) {
        const cachedDivisions = getDivisions();
        const newDiv = cachedDivisions?.find((d) => d.id === newDivisionId);
        triggerMismatch(newDivisionId, formatDivisionLabel(newDiv, newDivisionId));
      }
    };

    const handleDivisionValidated = (e: Event) => {
      if (!currentDivisionId) return;
      const response = (e as CustomEvent<V2SessionInfoResponse>).detail;
      if (!response.authenticated || !response.divisionId) return;
      if (response.divisionId !== currentDivisionId && !isDismissedMismatch(response.divisionId)) {
        // session-info doesn't return division metadata; look up the
        // human-readable name from the localStorage cache (populated on
        // mount). Fall back to the bare id when uncached.
        const cached = getDivisions();
        const newDiv = cached?.find(d => d.id === response.divisionId);
        triggerMismatch(response.divisionId, formatDivisionLabel(newDiv, response.divisionId));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(DIVISION_VALIDATED_EVENT, handleDivisionValidated);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(DIVISION_VALIDATED_EVENT, handleDivisionValidated);
    };
  }, [isV2, currentDivisionId]);

  // Restrict to divisions where the user has access to the current app.
  // Mirrors the active-division check in `useDivisionCheck`.
  const accessibleDivisions = useMemo(() => {
    if (!divisions) return [];
    return divisions.filter((d) => {
      const level = d.appAccess?.[appName];
      return Boolean(level) && level !== 'blocked';
    });
  }, [divisions, appName]);

  const filteredDivisions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matches = q
      ? accessibleDivisions.filter((d) => d.name?.toLowerCase().includes(q) || d.companyName?.toLowerCase().includes(q))
      : accessibleDivisions;
    // Sort alphabetically by company (then division name as tiebreaker), then
    // float the active division to the top.
    const sorted = matches.slice().sort((a, b) => {
      const ca = (a.companyName || a.name || '').toLowerCase();
      const cb = (b.companyName || b.name || '').toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb);
      return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
    });
    if (!currentDivisionId) return sorted;
    const activeIdx = sorted.findIndex((d) => d.id === currentDivisionId);
    if (activeIdx <= 0) return sorted;
    const [active] = sorted.splice(activeIdx, 1);
    sorted.unshift(active);
    return sorted;
  }, [accessibleDivisions, searchQuery, currentDivisionId]);

  const currentDivision = divisions?.find((d) => d.id === currentDivisionId);
  const showDivisionList = isV2 && accessibleDivisions.length > 1;

  const handleAvatarClick = () => {
    if (divisionMismatch) {
      setAlertAnchor(avatarRef.current);
      return;
    }
    setPopoverAnchor(avatarRef.current);
    setSearchQuery('');
    setView('menu');
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setSearchQuery('');
    setView('menu');
  };

  const handleCloseAlert = () => {
    setAlertAnchor(null);
  };

  const handleDivisionSwitch = useCallback(
    async (divisionId: string) => {
      try {
        const target = divisions?.find((d) => d.id === divisionId);
        const targetName = formatDivisionLabel(target, divisionId);
        beginSwitch(targetName);
        handleClosePopover();

        // Atomic in-place switch: single round trip; server revokes the old
        // JTI and sets the new sl-auth-token cookie via Set-Cookie. Replaces
        // the legacy v2Validate -> v2IssueToken two-call pattern.
        // Pass referrer so the server updates the per-app last-active key only
        // when the new division has access for the current app -- a future
        // login via the same app then prefers this division as the default.
        const tokenResponse = await v2SwitchDivision(divisionId, getSsoReferrer());
        saveCurrentDivisionId(divisionId);
        clearDismissedMismatch();

        // If the URL contains a /client/:id segment that belongs to the OLD
        // division (e.g. /client/915/sales/keymetrics), rewrite it to the new
        // division's default vendor. If the URL is bound to a division-specific
        // entity (e.g. /segment/:id, ?tab=advertising), reset to `/` so the
        // router lands on the new division's default page. We always call
        // buildPostSwitchUrl so Atlas-only divisions (no defaultBeaconClientId)
        // still get the entity-bound reset.
        const newUrl = buildPostSwitchUrl(tokenResponse.defaultBeaconClientId ?? null);
        if (newUrl) {
          window.location.href = newUrl;
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error('[AccountDivisionPopover] Failed to switch division:', error);
        endSwitch();
      }
    },
    [appName, divisions, beginSwitch, endSwitch]
  );

  const handleStay = async () => {
    setAlertAnchor(null);
    if (currentDivisionId) {
      await handleDivisionSwitch(currentDivisionId);
    }
  };

  const handleReloadWithNewDivision = () => {
    setAlertAnchor(null);
    clearDismissedMismatch();
    window.location.reload();
  };

  const handleSettings = () => {
    handleClosePopover();
    if (!window.location.pathname.includes('account')) {
      history.push(`/account/profile${searchParams}`);
    }
  };

  const handleSignOut = () => {
    handleClosePopover();
    history.push('/sign_out');
  };

  const popoverOpen = Boolean(popoverAnchor);
  const alertOpen = Boolean(alertAnchor);
  // #F6F8FA -- consistent with Settings/Sign out hover and admanager's
  // `hover:bg-grays-badge-bg`, so every hover affordance in the popover
  // matches. The previous secondaryPorcelain (`#f6f7f8`) was 1 channel off
  // and made the nested division list rows hover slightly differently
  // than the collapsed division row and footer buttons.
  const hoverBg = '#F6F8FA';
  const borderColor = theme.colors.primaryGray;

  return (
    <>
      {/* Avatar trigger */}
      <div
        ref={avatarRef}
        style={{ height: 30, width: 30, cursor: 'pointer', position: 'relative' }}
        role="button"
        onClick={handleAvatarClick}
      >
        <div
          style={{
            borderRadius: '50%',
            boxShadow: divisionMismatch ? '0 0 0 3px #F35379' : 'none',
            width: 30,
            height: 30,
            position: 'relative'
          }}
        >
          <AppProfileIcon shouldShowNotificationBadge={shouldShowNotificationBadge} size={30} />
          {divisionMismatch && (
            <div
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#F35379',
                border: '2px solid #F6F8FA'
              }}
            />
          )}
        </div>
      </div>

      {/* 2a: Division changed alert popover */}
      <Popover
        open={alertOpen}
        anchorEl={alertAnchor}
        onClose={handleCloseAlert}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{
          sx: {
            width: 320,
            borderRadius: '8px',
            border: '1px solid #dedede',
            boxShadow: '0 0 16px 0 rgba(0,0,0,0.06)',
            padding: '24px',
            marginTop: '12px',
            overflow: 'visible'
          }
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: '#FFEEED',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 12
          }}
        >
          {WARNING_ICON}
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, lineHeight: '24px', marginBottom: 8 }}>Division Changed</div>
        <div style={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.08px', marginBottom: 20 }}>
          You switched to <strong>{mismatchDivisionName}</strong> in another app. This page was loaded with{' '}
          <strong>{formatDivisionLabel(currentDivision, currentDivisionId || '')}</strong>.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleStay}
            style={{
              flex: 1,
              height: 32,
              background: '#fff',
              color: '#031425',
              border: '1px solid #E8E8ED',
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif'
            }}
          >
            Stay
          </button>
          <button
            onClick={handleReloadWithNewDivision}
            style={{
              flex: 1,
              height: 32,
              background: '#031425',
              color: '#fff',
              border: '1px solid #031425',
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif'
            }}
          >
            Reload Page
          </button>
        </div>
      </Popover>

      {/* 1a: Account + Division popover */}
      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{
          sx: {
            width: 280,
            borderRadius: '8px',
            border: `1px solid ${borderColor}`,
            boxShadow: '0px 4px 14px 0px rgba(0,0,0,0.1)',
            marginTop: '8px',
            overflow: 'hidden',
            padding: 0,
            // Default popover text color = #031425 (Figma primary). Most
            // child elements set their own color, but pinning a default
            // here mirrors Admanager's PopoverContent text-brand-primary
            // class so any unstyled text inherits the same dark blue.
            color: '#031425',
            // Scope font-smoothing to this popover only -- matches the
            // grayscale anti-aliasing applied globally in Admanager
            // (`antialiased` on <body>). Without this, macOS uses subpixel
            // anti-aliasing on this popover and the same `#7E8FA8 / 400 /
            // 12px` text renders visibly thicker/heavier than Admanager.
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            '& ul': { padding: 0 },
            // Search-divisions placeholder: Figma "Opacities/#031425 - 30"
            // = #B3B8BE. The previous #BEC7D3 was actually #7E8FA8 at 50%
            // (gray-blue tint), not the dark-blue-at-30% Figma asks for.
            '& input::placeholder': { color: '#B3B8BE', opacity: 1 },
            // Always-visible thin scrollbar on the nested division list.
            // macOS WebKit hides scrollbars by default (overlay style) so a
            // long division list silently scrolls with no affordance. Set the
            // ::-webkit-scrollbar pseudo-elements to force the classic mode.
            // `scrollbar-width: thin` is gated behind @supports not selector
            // (::-webkit-scrollbar) because Chrome 121+ treats any non-auto
            // value as opt-in to overlay mode and suppresses the
            // ::-webkit-scrollbar styling we just set.
            '& .sl-division-list-scroll': {
              '&::-webkit-scrollbar': { width: 5, height: 8 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(126, 143, 168, 0.5)',
                borderRadius: 9999,
                minHeight: 20
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(126, 143, 168, 0.7)'
              },
              '@supports not selector(::-webkit-scrollbar)': {
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(126, 143, 168, 0.5) transparent'
              }
            }
          }
        }}
      >
        {/* Email + Role header. Figma collapsed-popover spec
            (node 3061:9938): h:64, pl:14, pr:24, py:8, gap:4 between the
            two lines, content vertically centered. The email line uses
            Header/H5 (Roboto 700/14/20) -- the explicit lineHeight:'20px'
            on <Text> overrides MUI subtitle2's default 1.57 (~22px) so
            the row height matches Admanager and Figma. */}
        {view === 'menu' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 4,
              height: 64,
              padding: '8px 24px 8px 14px',
              borderBottom: `1px solid ${borderColor}`
            }}
          >
            <Text variant="subtitle2" fontWeight="bold" lineHeight="20px">
              {email}
            </Text>
            <div
              style={{
                // #8293A4 -- Figma "Opacities/#052849 - 50". Apply the muted
                // color directly rather than #7E8FA8 + opacity:0.5 (which
                // resolves to #BEC7D3 on white).
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '18px',
                letterSpacing: '0.08px',
                color: '#8293A4'
              }}
            >
              {role}
            </div>
          </div>
        )}

        {/* Collapsed division row — drills into the nested division view.
            Hidden when only one accessible division exists, matching the
            previous list-hidden behavior. */}
        {view === 'menu' && showDivisionList && (
          <div
            onClick={() => { setView('divisions'); setSearchQuery(''); }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setView('divisions');
                setSearchQuery('');
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              height: 48,
              padding: '8px 8px 8px 14px',
              borderBottom: `1px solid ${borderColor}`,
              cursor: 'pointer',
              background: '#fff',
              transition: 'background 0.1s'
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#F6F8FA'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#fff'; }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '20px',
                  letterSpacing: '0.08px',
                  color: '#031425',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {currentDivision?.companyName || currentDivision?.name || 'Select division'}
              </div>
              {currentDivision?.companyName && currentDivision?.name && (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: '18px',
                    letterSpacing: '0.08px',
                    color: '#7E8FA8',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {currentDivision.name}
                </div>
              )}
            </div>
            {CHEVRON_RIGHT}
          </div>
        )}

        {/* Nested division view: back row, search, then list (cap at 8
            visible × 48px = 384px before scroll attaches). */}
        {view === 'divisions' && (
          <>
            <div
              onClick={() => { setView('menu'); setSearchQuery(''); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setView('menu');
                  setSearchQuery('');
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '8px 12px',
                borderBottom: `1px solid ${borderColor}`,
                cursor: 'pointer',
                background: '#fff',
                transition: 'background 0.1s'
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#F6F8FA'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#fff'; }}
            >
              {CHEVRON_LEFT}
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '20px',
                  letterSpacing: '0.08px',
                  color: '#031425',
                  fontFamily: 'Roboto, sans-serif'
                }}
              >
                Back
              </div>
            </div>
            <div
              style={{
                height: 40,
                padding: '0 12px',
                borderBottom: `1px solid ${borderColor}`,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {SEARCH_ICON}
              <input
                type="text"
                placeholder="Search divisions"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  width: '100%',
                  height: '100%',
                  fontSize: 14,
                  color: '#031425',
                  fontFamily: 'Roboto, sans-serif'
                }}
              />
            </div>
            <div className="sl-division-list-scroll" style={{ maxHeight: 384, overflowY: 'auto', background: '#fff' }}>
              {filteredDivisions.map((division, index) => {
                const isSelected = division.id === currentDivisionId;
                const isLast = index === filteredDivisions.length - 1;
                return (
                  <div
                    key={division.id}
                    onClick={() => { if (division.id !== currentDivisionId) handleDivisionSwitch(division.id); }}
                    style={{
                      // Outer flex row + inner flex column with min-width:0 +
                      // flex:1 -- this is the only ellipsis-safe layout for a
                      // 280px popover with long company names. A direct flex
                      // column on the row lets the row's content push past the
                      // popover width without clipping (text-overflow only
                      // triggers when the element is actually width-constrained).
                      display: 'flex',
                      alignItems: 'center',
                      height: 48,
                      padding: '8px 24px 8px 14px',
                      cursor: isSelected ? 'default' : 'pointer',
                      borderBottom: isLast ? 'none' : `1px solid ${borderColor}`,
                      background: isSelected ? '#EDF5FF' : '#fff',
                      transition: 'background 0.1s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) (e.currentTarget as HTMLElement).style.background = hoverBg;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = isSelected ? '#EDF5FF' : '#fff';
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: '20px',
                          letterSpacing: '0.08px',
                          color: '#031425',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {division.companyName || division.name || division.id}
                      </div>
                      {division.companyName && division.name && (
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: '18px',
                            letterSpacing: '0.08px',
                            color: '#7E8FA8',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {division.name}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {filteredDivisions.length === 0 && (
                <div style={{ padding: '12px 20px', fontSize: 13, color: '#7E8FA8', textAlign: 'center' }}>
                  No divisions found
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer actions (unchanged). The previous borderTop + marginTop:4
            on this wrapper was paired with the embedded scroll list
            (separator between the scrollable list and the static footer).
            With the nested picker, the divider above Settings comes from
            the collapsed division row's borderBottom (or the email/role
            row's borderBottom when only one division), so the wrapper-level
            border would render as a duplicate line. */}
        {view === 'menu' && (
        <div>
          <button
            onClick={handleSettings}
            style={{
              // Figma 3061:9946 (Settings row): h:48, pl:14, pr:24, py:8.
              // Switched from `display: block` + ad-hoc padding to a flex
              // row so the height/padding match the Figma spec exactly and
              // the optional NotificationBadge sits centered next to the
              // label rather than baselined inline.
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: 48,
              padding: '8px 24px 8px 14px',
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.08px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif',
              color: '#031425',
              textAlign: 'left',
              transition: 'background 0.1s'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = '#F6F8FA';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
          >
            Settings
            {shouldShowNotificationBadge && <NotificationBadge variant="small" />}
          </button>
          <button
            onClick={handleSignOut}
            style={{
              // Figma 3061:9948 (Sign out row): h:48, pl:14, pr:24, py:8.
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: 48,
              padding: '8px 24px 8px 14px',
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.08px',
              borderWidth: '1px 0 0 0',
              borderStyle: 'solid',
              borderColor: borderColor,
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif',
              color: '#031425',
              textAlign: 'left',
              transition: 'background 0.1s'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = '#F6F8FA';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
          >
            Sign out
          </button>
        </div>
        )}
      </Popover>
    </>
  );
};

export default AccountDivisionPopover;
